import { addDays } from './local-date.js';

/**
 * Gemi saati kaydı (Ship's Clock Log; ADR 0065). Saf fonksiyonlar: geminin IANA dilimi ve
 * **onaylı** saat değişiklikleri parametreyle gelir; "şimdi" de.
 *
 * Gemi saati kaptanın emriyle ileri ya da geri alınır. Çalışma ve dinlenme gerçekte geçen süreden
 * hesaplanır [MLC A2.3 ¶5; STCW A-VIII/1 §2]; bu modül yalnız saatin hangi anda hangi farkla
 * gösterildiğini ve gemi gününün sınırlarını verir. Uygunluk denetimi `rest-hours.ts`'te kalır.
 */

const MINUTE = 60_000;
const DAY_MS = 86_400_000;

export const CLOCK_LIMITS = {
  /** UTC farkının sınırları (dakika): −12:00 … +14:00. */
  minOffset: -720,
  maxOffset: 840,
  /** Bu büyüklükte (ve üstü) değişiklik tarih değiştirme çizgisidir (dakika). */
  dateLineMin: 720,
  /** Geri almada en çok 24 saat: gün en çok iki kez yaşanır (dakika). */
  maxRetardMin: 1440,
  /** İleri tarihli kayıt en çok bu kadar gün sonrası için girilir. */
  maxAheadDays: 31,
  /** Açıklamanın en çok uzunluğu. */
  reasonMax: 500,
} as const;

/** Bir saat değişikliği: gerçek an (UTC) ve öncesi/sonrası fark (dakika). */
export interface ClockChange {
  effectiveAt: string;
  oldOffsetMin: number;
  newOffsetMin: number;
}

interface Step {
  at: number;
  old: number;
  next: number;
}

/** Saat çizelgesi: dilim ve zaman sırasıyla onaylı değişiklikler. */
export interface ClockSchedule {
  timeZone: string;
  changes: readonly Step[];
}

/** Dilim başına tek biçimlendirici: `Intl.DateTimeFormat` kurmak pahalıdır. */
const FORMATS = new Map<string, Intl.DateTimeFormat>();

/** IANA diliminin verilen andaki UTC farkı (dakika). Bilinmeyen dilim 0. */
export function ianaOffsetMinutes(timeZone: string, at: number): number {
  let format = FORMATS.get(timeZone);
  if (!format) {
    try {
      format = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'longOffset' });
    } catch {
      return 0;
    }
    FORMATS.set(timeZone, format);
  }
  const part = format.formatToParts(new Date(at)).find((p) => p.type === 'timeZoneName')?.value;
  const match = /GMT([+-])(\d{2}):(\d{2})/.exec(part ?? '');
  if (!match) return 0;
  const minutes = Number(match[2]) * 60 + Number(match[3]);
  return match[1] === '-' ? -minutes : minutes;
}

/** Onaylı değişikliklerden saat çizelgesi (sıra önemsiz; ana göre sıralanır). */
export function clockSchedule(
  timeZone: string,
  changes: readonly ClockChange[] = [],
): ClockSchedule {
  return {
    timeZone,
    changes: changes
      .map((c) => ({ at: Date.parse(c.effectiveAt), old: c.oldOffsetMin, next: c.newOffsetMin }))
      .filter((c) => !Number.isNaN(c.at))
      .sort((a, b) => a.at - b.at),
  };
}

/**
 * Gemi saatinin kaynağı: geminin IANA dilimi (saat kaydı okunmadıysa) ya da saat çizelgesi.
 * Çizelge düz veridir; API'de olduğu gibi taşınır (ADR 0065 §4).
 */
export type ShipTime = string | ClockSchedule;

export function asClockSchedule(time: ShipTime): ClockSchedule {
  return typeof time === 'string' ? clockSchedule(time) : time;
}

/** Gemi saatinin `at` anındaki farkı (dakika): dilimden ya da saat kaydından. */
export function shipTimeOffset(time: ShipTime, at: number): number {
  return typeof time === 'string' ? ianaOffsetMinutes(time, at) : clockOffsetAt(time, at);
}

/** Gemi saatinin `at` anındaki yerel tarihi (`YYYY-MM-DD`). */
export function shipTimeLocalDate(time: ShipTime, at: number): string {
  return new Date(at + shipTimeOffset(time, at) * MINUTE).toISOString().slice(0, 10);
}

/** Dilimin adı (gösterim). */
export function shipTimeZoneName(time: ShipTime): string {
  return typeof time === 'string' ? time : time.timeZone;
}

/** `t` anında geçerli fark: son onaylı değişikliğin yeni farkı; yoksa dilimin farkı. */
export function clockOffsetAt(schedule: ClockSchedule, at: number): number {
  let offset: number | null = null;
  for (const step of schedule.changes) {
    if (step.at <= at) offset = step.next;
    else break;
  }
  return offset ?? ianaOffsetMinutes(schedule.timeZone, at);
}

/** `t` anının gemi yerel tarihi (`YYYY-MM-DD`). */
export function clockLocalDate(schedule: ClockSchedule, at: number): string {
  return new Date(at + clockOffsetAt(schedule, at) * MINUTE).toISOString().slice(0, 10);
}

const wallMidnight = (date: string) => Date.parse(`${date}T00:00:00.000Z`);

/** Dilimin gün başı (eski hesap; `localDayStart` ile aynı). */
function zoneDayStart(timeZone: string, date: string): number {
  const midnight = wallMidnight(date);
  const guess = midnight - ianaOffsetMinutes(timeZone, midnight) * MINUTE;
  return midnight - ianaOffsetMinutes(timeZone, guess) * MINUTE;
}

/**
 * `date` gemi gününün başladığı an (ms): yerel saatin `date 00:00`'ı aşağıdan **son** geçtiği an
 * (ADR 0065 §4). Saat geri alınan günde gün 25, ileri alınan günde 23 saat sürer.
 */
export function clockDayStart(schedule: ClockSchedule, date: string): number {
  const midnight = wallMidnight(date);
  if (Number.isNaN(midnight)) throw new RangeError(`INVALID_DATE ${date}`);
  const steps = schedule.changes;
  const first = steps[0];
  const candidates: number[] = [];
  // Dilimin yönettiği ilk parça: ilk değişiklikten önce
  const zone = zoneDayStart(schedule.timeZone, date);
  if (!first || zone < first.at) candidates.push(zone);
  steps.forEach((step, i) => {
    // Sabit farklı parçanın içinde (başında değil) geçiş
    const t = midnight - step.next * MINUTE;
    const end = steps[i + 1]?.at ?? Number.POSITIVE_INFINITY;
    if (t > step.at && t < end) candidates.push(t);
    // Değişiklik anında ileri sıçrayarak geçiş (sol limit `at + old` M'nin altında ya da eşit)
    if (step.at + step.old * MINUTE <= midnight && midnight <= step.at + step.next * MINUTE) {
      candidates.push(step.at);
    }
  });
  if (candidates.length === 0) return zone;
  return Math.max(...candidates);
}

/** Bir gemi günü (ya da tarih çizgisinde bir gün oluşumu). `RestDay` ile uyumlu. */
export interface ShipDay {
  date: string;
  /** Takvim günü iki kez yaşandıysa (tarih çizgisi) 1 ya da 2. */
  occurrence?: 1 | 2;
  start: string;
  end: string;
  /** Günün gerçek süresi (dakika). */
  minutes: number;
  /** Tarih çizgisinde atlanan gün: sıfır uzunluk, dinlenme denetimine girmez. */
  skipped?: true;
}

/** Bir günün oluşumları: çoğunlukla bir; tarih çizgisinde geri almada iki; atlanan gün boş. */
export function clockDaysOf(schedule: ClockSchedule, date: string): ShipDay[] {
  const start = clockDayStart(schedule, date);
  const end = clockDayStart(schedule, addDays(date, 1));
  if (end <= start) {
    const at = new Date(start).toISOString();
    return [{ date, start: at, end: at, minutes: 0, skipped: true }];
  }
  // Tarih çizgisi geri almaları günü oluşumlara böler
  const splits = schedule.changes
    .filter((s) => s.next - s.old <= -CLOCK_LIMITS.dateLineMin && s.at > start && s.at < end)
    .map((s) => s.at);
  const bounds = [start, ...splits, end];
  const days: ShipDay[] = [];
  for (let i = 0; i + 1 < bounds.length; i += 1) {
    const from = bounds[i] as number;
    const to = bounds[i + 1] as number;
    days.push({
      date,
      ...(splits.length > 0 ? { occurrence: (i === 0 ? 1 : 2) as 1 | 2 } : {}),
      start: new Date(from).toISOString(),
      end: new Date(to).toISOString(),
      minutes: Math.round((to - from) / MINUTE),
    });
  }
  return days;
}

/** `from`–`to` gemi günleri, sırayla (atlanan günler `skipped` ile dahil). */
export function clockDays(schedule: ClockSchedule, from: string, to: string): ShipDay[] {
  const days: ShipDay[] = [];
  for (let date = from; date <= to; date = addDays(date, 1)) {
    days.push(...clockDaysOf(schedule, date));
  }
  return days;
}

/**
 * Duvar saati `date` + `minute` (gemi yerel) hangi anlarda yaşandı: hiç (ileri almada atlanan),
 * bir ya da iki kez (geri almada tekrar eden). Sıralı.
 */
export function wallInstants(schedule: ClockSchedule, date: string, minute: number): number[] {
  return wallInstantsMs(schedule, wallMidnight(date) + minute * MINUTE);
}

function wallInstantsMs(schedule: ClockSchedule, wall: number): number[] {
  const found = new Set<number>();
  const steps = schedule.changes;
  const first = steps[0];
  const zoneOffsets = new Set(
    [-18, 0, 18].map((h) => ianaOffsetMinutes(schedule.timeZone, wall + h * 3_600_000)),
  );
  for (const offset of zoneOffsets) {
    const t = wall - offset * MINUTE;
    if (ianaOffsetMinutes(schedule.timeZone, t) === offset && (!first || t < first.at)) {
      found.add(t);
    }
  }
  steps.forEach((step, i) => {
    const t = wall - step.next * MINUTE;
    const end = steps[i + 1]?.at ?? Number.POSITIVE_INFINITY;
    if (t >= step.at && t < end) found.add(t);
  });
  return [...found].sort((a, b) => a - b);
}

/** Anın duvar saati: tarih, gün içi dakika ve saat tekrar ediyorsa kaçıncı oluşumu. */
export function wallLabel(
  schedule: ClockSchedule,
  at: number,
): { date: string; minute: number; occurrence: 1 | 2 | null } {
  const wall = at + clockOffsetAt(schedule, at) * MINUTE;
  const date = new Date(wall).toISOString().slice(0, 10);
  const minute = Math.floor((wall - wallMidnight(date)) / MINUTE);
  const all = wallInstantsMs(schedule, wall);
  const index = all.indexOf(at);
  return {
    date,
    minute,
    occurrence: all.length > 1 && index >= 0 ? ((Math.min(index, 1) + 1) as 1 | 2) : null,
  };
}

/**
 * Bitiş adayları: başlangıçtan sonra duvar saatinin `date` ya da ertesi gün `minute`'u gösterdiği
 * anlar, sırayla. İlki varsayılandır; ilk iki aday aynı duvar saatiyse (tekrar eden saat) kişi
 * seçer.
 */
export function endCandidates(
  schedule: ClockSchedule,
  start: number,
  date: string,
  minute: number,
): number[] {
  const all = [
    ...wallInstants(schedule, date, minute),
    ...wallInstants(schedule, addDays(date, 1), minute),
  ].filter((t) => t > start);
  const first = all[0];
  if (first === undefined) return [];
  const firstWall = first + clockOffsetAt(schedule, first) * MINUTE;
  // Aynı duvar saatinin oluşumları (en çok iki)
  return all.filter((t) => t + clockOffsetAt(schedule, t) * MINUTE === firstWall).slice(0, 2);
}

/** Günün bir parçası: sabit farklı gerçek zaman aralığı (dakika, gün başından). */
export interface DayPiece {
  from: number;
  to: number;
  /** Parçanın başındaki duvar saati, gün tarihinin gece yarısından dakika. */
  wallFrom: number;
  offsetMin: number;
}

export interface DayAdjustment {
  /** Gerçek dakika, gün başından. */
  at: number;
  oldOffsetMin: number;
  newOffsetMin: number;
  changeMin: number;
  /** Değişiklik anında eski saatin gösterdiği duvar saati (gün tarihinden dakika). */
  wallBefore: number;
}

export interface DayTimeline {
  day: ShipDay;
  pieces: DayPiece[];
  adjustments: DayAdjustment[];
  /** İleri almada yaşanmayan duvar saati aralıkları; `at` gerçek dakikadır. */
  skipped: { at: number; wallFrom: number; wallTo: number }[];
  /** Geri almada iki kez yaşanan duvar saati aralıkları. */
  repeated: { wallFrom: number; wallTo: number }[];
}

/** Dilimin `from`–`to` içindeki fark geçişleri (yaz saati); en çok biri gün içindedir. */
function zoneTransitions(timeZone: string, from: number, to: number): Step[] {
  const steps: Step[] = [];
  for (let a = from; a < to; a += 12 * 3_600_000) {
    const b = Math.min(to, a + 12 * 3_600_000);
    const old = ianaOffsetMinutes(timeZone, a);
    const next = ianaOffsetMinutes(timeZone, b);
    if (old === next) continue;
    let lo = a;
    let hi = b;
    while (hi - lo > MINUTE) {
      const mid = Math.floor((lo + hi) / 2 / MINUTE) * MINUTE;
      if (mid <= lo) break;
      if (ianaOffsetMinutes(timeZone, mid) === old) lo = mid;
      else hi = mid;
    }
    steps.push({ at: hi, old, next });
  }
  return steps;
}

/** Günün çizelgesi: parçalar, saat değişikliği işaretleri, atlanan ve tekrar eden aralıklar. */
export function dayTimeline(schedule: ClockSchedule, day: ShipDay): DayTimeline {
  const start = Date.parse(day.start);
  const end = Date.parse(day.end);
  const midnight = wallMidnight(day.date);
  const first = schedule.changes[0];
  const zoneEnd = Math.min(end, first ? first.at : end);
  const steps = [
    ...(start < zoneEnd ? zoneTransitions(schedule.timeZone, start, zoneEnd) : []),
    ...schedule.changes,
  ].filter((s) => s.at >= start && s.at < end);
  const cuts = [start, ...steps.map((s) => s.at).filter((at) => at > start), end];
  const pieces: DayPiece[] = [];
  for (let i = 0; i + 1 < cuts.length; i += 1) {
    const from = cuts[i] as number;
    const to = cuts[i + 1] as number;
    const offsetMin = clockOffsetAt(schedule, from);
    pieces.push({
      from: Math.round((from - start) / MINUTE),
      to: Math.round((to - start) / MINUTE),
      wallFrom: Math.round((from + offsetMin * MINUTE - midnight) / MINUTE),
      offsetMin,
    });
  }
  const adjustments = steps.map((s) => ({
    at: Math.round((s.at - start) / MINUTE),
    oldOffsetMin: s.old,
    newOffsetMin: s.next,
    changeMin: s.next - s.old,
    wallBefore: Math.round((s.at + s.old * MINUTE - midnight) / MINUTE),
  }));
  const skipped: DayTimeline['skipped'] = [];
  const repeated: DayTimeline['repeated'] = [];
  const firstPiece = pieces[0];
  if (firstPiece && firstPiece.wallFrom > 0 && day.skipped !== true) {
    skipped.push({ at: 0, wallFrom: 0, wallTo: Math.min(firstPiece.wallFrom, 1440) });
  }
  pieces.forEach((piece, i) => {
    const previous = pieces[i - 1];
    if (!previous) return;
    const previousEnd = previous.wallFrom + (previous.to - previous.from);
    if (piece.wallFrom > previousEnd) {
      skipped.push({ at: piece.from, wallFrom: previousEnd, wallTo: piece.wallFrom });
    }
    // Önceki parçaların kapsadığı duvar saatine geri dönüş
    for (const earlier of pieces.slice(0, i)) {
      const lo = Math.max(earlier.wallFrom, piece.wallFrom);
      const hi = Math.min(
        earlier.wallFrom + (earlier.to - earlier.from),
        piece.wallFrom + (piece.to - piece.from),
      );
      if (hi > lo) repeated.push({ wallFrom: lo, wallTo: hi });
    }
  });
  const last = pieces.at(-1);
  if (last) {
    const lastEnd = last.wallFrom + (last.to - last.from);
    // Gün içinde ileri almayla biten gün: kalan duvar saati yaşanmadı (ör. 23:30 → 00:30)
    if (lastEnd < 1440 && end < Number.POSITIVE_INFINITY && day.occurrence !== 1) {
      const next = clockOffsetAt(schedule, end);
      if (end + next * MINUTE > midnight + 1440 * MINUTE) {
        skipped.push({ at: last.to, wallFrom: lastEnd, wallTo: 1440 });
      }
    }
  }
  return { day, pieces, adjustments, skipped, repeated };
}

/** Değişikliğin günü: anını içeren gemi günü; atlanan gün varsa o (ADR 0065 §6). */
export function clockChangeDay(schedule: ClockSchedule, step: { at: number }): string {
  const at = step.at;
  const date = new Date(at).toISOString().slice(0, 10);
  for (const d of clockDays(schedule, addDays(date, -2), addDays(date, 2))) {
    const from = Date.parse(d.start);
    const to = Date.parse(d.end);
    if ((d.skipped && from === at) || (from <= at && at < to)) return d.date;
  }
  return clockLocalDate(schedule, at);
}

export type ClockMonthNote =
  | {
      kind: 'change';
      /** Değişikliğin gemi günü. */
      date: string;
      effectiveAt: string;
      /** Eski saatle değişiklik anı, `YYYY-MM-DDTHH:MM`. */
      wallBefore: string;
      oldOffsetMin: number;
      newOffsetMin: number;
      changeMin: number;
    }
  | { kind: 'repeated' | 'skipped'; date: string };

/** Aylık kaydın otomatik notları: aya düşen değişiklikler ve tarih çizgisi günleri (§7). */
export function clockMonthNotes(schedule: ClockSchedule, month: string): ClockMonthNote[] {
  const notes: ClockMonthNote[] = [];
  for (const step of schedule.changes) {
    const date = clockChangeDay(schedule, step);
    if (date.slice(0, 7) !== month) continue;
    notes.push({
      kind: 'change',
      date,
      effectiveAt: new Date(step.at).toISOString(),
      wallBefore: new Date(step.at + step.old * MINUTE).toISOString().slice(0, 16),
      oldOffsetMin: step.old,
      newOffsetMin: step.next,
      changeMin: step.next - step.old,
    });
    if (Math.abs(step.next - step.old) >= CLOCK_LIMITS.dateLineMin) {
      for (const d of clockDays(schedule, addDays(date, -1), addDays(date, 2))) {
        if (d.date.slice(0, 7) !== month) continue;
        if (d.skipped) notes.push({ kind: 'skipped', date: d.date });
        if (d.occurrence === 2) notes.push({ kind: 'repeated', date: d.date });
      }
    }
  }
  return notes;
}

/** `UTC+03:00`; eksi U+2212, sıfır `UTC±00:00` (masaüstü üst çubuğuyla aynı biçim). */
export function formatUtcOffset(minutes: number): string {
  if (minutes === 0) return 'UTC±00:00';
  const abs = Math.abs(minutes);
  const sign = minutes > 0 ? '+' : '−';
  return `UTC${sign}${String(Math.floor(abs / 60)).padStart(2, '0')}:${String(abs % 60).padStart(2, '0')}`;
}

/**
 * Deniz saati (zone time) önerisi: boylamdan `round(boylam / 15)` saat; ±180°'de ±12
 * [Bowditch, American Practical Navigator, "Zone Time"]. Dakika.
 */
export function nauticalZoneOffset(longitude: number): number {
  const zone = Math.round(longitude / 15);
  return Math.max(-12, Math.min(12, zone)) * 60 + 0;
}

export type ClockChangeError =
  | 'INVALID_DATE'
  | 'OFFSET_OUT_OF_RANGE'
  | 'CLOCK_CHANGE_ZERO'
  | 'CHANGE_MISMATCH'
  | 'CLOCK_RETARD_TOO_LARGE'
  | 'DATE_LINE_NOT_MIDNIGHT'
  | 'CLOCK_RETARD_ACROSS_MIDNIGHT'
  | 'CLOCK_CHANGE_TOO_FAR'
  | 'REASON_REQUIRED'
  | 'TOO_LONG';

export interface ClockChangeInput extends ClockChange {
  changeMin: number;
  reason: string;
}

const inRange = (value: unknown) =>
  Number.isInteger(value) &&
  (value as number) >= CLOCK_LIMITS.minOffset &&
  (value as number) <= CLOCK_LIMITS.maxOffset;

/**
 * Tek kaydın denetimi (ADR 0065 §1). Zincir (önceki kayıtla süreklilik ve sıra) veritabanı
 * kuralındadır. `now`: gemi sunucusu saati.
 */
export function validateClockChange(input: ClockChangeInput, now: string): ClockChangeError[] {
  const errors = new Set<ClockChangeError>();
  const at = Date.parse(input.effectiveAt);
  if (Number.isNaN(at) || at % MINUTE !== 0 || Number.isNaN(Date.parse(now))) {
    errors.add('INVALID_DATE');
  }
  if (!inRange(input.oldOffsetMin) || !inRange(input.newOffsetMin)) {
    errors.add('OFFSET_OUT_OF_RANGE');
  }
  const change = input.newOffsetMin - input.oldOffsetMin;
  if (change === 0) errors.add('CLOCK_CHANGE_ZERO');
  if (input.changeMin !== change) errors.add('CHANGE_MISMATCH');
  if (change < -CLOCK_LIMITS.maxRetardMin) errors.add('CLOCK_RETARD_TOO_LARGE');
  if (!errors.has('INVALID_DATE') && !errors.has('OFFSET_OUT_OF_RANGE')) {
    const wall = at + input.oldOffsetMin * MINUTE;
    const minuteOfDay = Math.round((((wall % DAY_MS) + DAY_MS) % DAY_MS) / MINUTE);
    if (Math.abs(change) >= CLOCK_LIMITS.dateLineMin) {
      if (minuteOfDay !== 0) errors.add('DATE_LINE_NOT_MIDNIGHT');
    } else if (change < 0 && minuteOfDay !== 0 && minuteOfDay + change < 0) {
      errors.add('CLOCK_RETARD_ACROSS_MIDNIGHT');
    }
    if (at > Date.parse(now) + CLOCK_LIMITS.maxAheadDays * DAY_MS) {
      errors.add('CLOCK_CHANGE_TOO_FAR');
    }
  }
  const reason = typeof input.reason === 'string' ? input.reason.trim() : '';
  if (reason === '') errors.add('REASON_REQUIRED');
  else if (reason.length > CLOCK_LIMITS.reasonMax) errors.add('TOO_LONG');
  return [...errors];
}
